import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ApiClient } from '@accounting/shared';
import { ApiError, companyClient, sessionClient, signOut as apiSignOut } from '../api/client.js';

/**
 * Who is signed in, and which company they are acting as.
 *
 * Two layers, mirroring the API:
 *
 * - **Better Auth session** — a cookie, 30 days, owned by the browser. Not
 *   readable from JavaScript, which is the point; the UI never holds it.
 * - **Company access token** — a JWT, 15 minutes, one company. Held in memory
 *   only.
 *
 * ## Why the token is not in localStorage
 *
 * A token in localStorage is readable by any script that ends up on the page,
 * and survives the tab. Keeping it in memory means a refresh re-derives it from
 * the session cookie, which is the credential that is actually meant to persist
 * and which the browser protects. The cost is one extra request on load; the
 * alternative is a bearer token for a financial ledger sitting in a store that
 * XSS can read.
 */

export interface Membership {
  readonly companyId: string;
  readonly role: string;
  readonly name: string | null;
}

export interface AuthState {
  /** Still working out whether there is a session. Nothing is known yet. */
  readonly loading: boolean;
  readonly memberships: readonly Membership[];
  readonly companyId: string | null;
  readonly role: string | null;
  /** A client bound to the current company token, or null when not signed in. */
  readonly api: ApiClient | null;
  /**
   * A Better Auth session exists.
   *
   * Deliberately not the same as holding a company token. A user who is a
   * member of no company has a perfectly good session and no token, and
   * conflating the two sends them to a login form they complete successfully
   * only to arrive back at it.
   */
  readonly hasSession: boolean;
  /** A company has been chosen and its token is held. */
  readonly signedIn: boolean;
  /**
   * Establishing the session failed for a reason that is not "not signed in".
   *
   * A 401 is an answer and leaves this null. Anything else -- the API down, a
   * network blip, a CORS misconfiguration -- is a fault, and treating it as
   * "not signed in" would send someone to a login form that cannot work either
   * and tell them nothing about why.
   */
  readonly error: unknown;
}

export interface AuthActions {
  /**
   * Re-read memberships and pick up a company. Call after signing in.
   *
   * Resolves to whether a session was established.
   *
   * The caller cannot learn this from `hasSession` straight afterwards — that
   * is React state and does not update until the next render — so the answer is
   * returned. The login form needs it: a sign-in can succeed while no session
   * survives, and it has to say so rather than navigate away and bounce back.
   */
  readonly refresh: () => Promise<boolean>;
  readonly selectCompany: (companyId: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

export type AuthContextValue = AuthState & AuthActions;

export const AuthContext = createContext<AuthContextValue | null>(null);

interface TokenResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly companyId: string;
  readonly role: string;
}

/** The company last chosen, so a reload does not land on a different one. */
const LAST_COMPANY_KEY = 'accounting.lastCompanyId';

function readLastCompany(): string | null {
  try {
    return globalThis.localStorage?.getItem(LAST_COMPANY_KEY) ?? null;
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    // Remembering the last company is a convenience, never a requirement.
    return null;
  }
}

function writeLastCompany(companyId: string): void {
  try {
    globalThis.localStorage?.setItem(LAST_COMPANY_KEY, companyId);
  } catch {
    // As above.
  }
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [loading, setLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [memberships, setMemberships] = useState<readonly Membership[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);

  // Cleared on sign-out and on every re-issue, so a stale timer cannot resurrect
  // a token for a company the user has left.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefresh = useCallback(() => {
    if (refreshTimer.current !== null) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  /**
   * Exchange the session for a token, and schedule the next exchange.
   *
   * Renewal is at 80 % of the lifetime rather than on expiry: a token that
   * expires mid-request produces a 401 the user has to understand, and there is
   * no reason to make them.
   */
  const issueToken = useCallback(
    async (target: string, endpoint: '/v1/auth/session/company' | '/v1/auth/session/refresh') => {
      const response = await sessionClient().request<TokenResponse>(endpoint, {
        method: 'POST',
        body: { companyId: target },
      });

      setToken(response.accessToken);
      setCompanyId(response.companyId);
      setRole(response.role);
      writeLastCompany(response.companyId);

      clearRefresh();
      refreshTimer.current = setTimeout(
        () => {
          void issueToken(response.companyId, '/v1/auth/session/refresh').catch(() => {
            // The session has expired or the membership was revoked. Drop the
            // token; the protected route boundary sends the user to sign in.
            setToken(null);
            setCompanyId(null);
            setRole(null);
          });
        },
        Math.max(response.expiresIn * 0.8, 30) * 1000,
      );
    },
    [clearRefresh],
  );

  const refresh = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await sessionClient().request<{ items: Membership[] }>(
        '/v1/auth/companies',
      );
      // The call succeeding is what proves there is a session; the list being
      // empty says something else entirely.
      setHasSession(true);
      setMemberships(items);

      if (items.length === 0) {
        setToken(null);
        setCompanyId(null);
        setRole(null);
        // A session with no memberships is still a session.
        return true;
      }

      const remembered = readLastCompany();
      const chosen =
        items.find((item) => item.companyId === remembered)?.companyId ??
        items[0]?.companyId;
      if (chosen !== undefined) {
        await issueToken(chosen, '/v1/auth/session/company');
      }
      return true;
    } catch (caught) {
      // A 401 here is the ordinary "not signed in" case, not a failure worth
      // showing. Anything else is recorded so the boundary can say what went
      // wrong and offer to try again, rather than silently redirecting to a
      // login form that would fail the same way.
      if (!(caught instanceof ApiError && caught.status === 401)) {
        setError(caught);
        setHasSession(false);
        return false;
      }
      setHasSession(false);
      setMemberships([]);
      setToken(null);
      setCompanyId(null);
      setRole(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [issueToken]);

  const selectCompany = useCallback(
    async (target: string) => {
      // Switching issues a new token rather than mutating the session, so a
      // token always authorises exactly one company.
      await issueToken(target, '/v1/auth/session/company');
    },
    [issueToken],
  );

  const signOut = useCallback(async () => {
    /*
      The server first, and nothing cleared until it has answered.

      If it refuses or cannot be reached the session is still valid — the
      cookie is httpOnly and only the server can end it — so the person is
      still signed in, and the state here has to keep saying so. The refresh
      timer is left running for the same reason: dropping it would let the
      token lapse under someone who is, in fact, signed in.

      The rejection propagates so the caller can tell them.
    */
    await apiSignOut();
    clearRefresh();
    setToken(null);
    setCompanyId(null);
    setRole(null);
    setMemberships([]);
    setHasSession(false);
    setError(null);
  }, [clearRefresh]);

  useEffect(() => {
    void refresh().catch(() => {
      setLoading(false);
    });
    return clearRefresh;
    // Deliberately once, on mount: this establishes the session, and re-running
    // it whenever `refresh` changes identity would re-authenticate on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      hasSession,
      error,
      memberships,
      companyId,
      role,
      api: token === null ? null : companyClient(token),
      signedIn: token !== null,
      refresh,
      selectCompany,
      signOut,
    }),
    [
      loading,
      hasSession,
      error,
      memberships,
      companyId,
      role,
      token,
      refresh,
      selectCompany,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
